import {
  DisplaySettingsColumn,
  DisplaySettingsFilter,
  DisplaySettingsSort,
  GridDisplaySettings
} from '@app/models';
import { getDisplaySettingsField } from '@app/pcs/util/grid/mappings';
import { DataGridColumnWithId } from 'ngrx-data-grid';
import * as R from 'ramda';

const findIndexByField = (field: string, columns) => R.findIndex(R.propEq('field', field), columns);
const indexFound = R.lte(0);

const updateColumns = (updatedGridColumn: DataGridColumnWithId, columns: DisplaySettingsColumn[] = []): DisplaySettingsColumn[] => {
  const {field, visible} = updatedGridColumn;
  const width: number = updatedGridColumn?.width;
  const filedName = getDisplaySettingsField(field);
  const columnIndex = findIndexByField(filedName, columns);

  const update = indexFound(columnIndex)
    ? R.update(columnIndex, {
      ...columns[columnIndex],
      hidden: !visible,
      width
    })
    : R.append({
      field: filedName,
      hidden: !visible,
      width
    });

  return update(columns);
};

const getUpdatedFilter = ({field, filter}: DataGridColumnWithId): DisplaySettingsFilter => ({
  field: getDisplaySettingsField(field),
  value: filter.value,
  operator: filter.option
});

const updateFilters = (updatedGridColumn: DataGridColumnWithId, filters: DisplaySettingsFilter[] = []): DisplaySettingsFilter[] => {
  const {field, filter, filterAvailable} = updatedGridColumn;

  if (!filterAvailable) {
    return filters;
  }

  const filterIndex = findIndexByField(getDisplaySettingsField(field), filters);

  const update: any = indexFound(filterIndex)
    ? filter.value
      ? R.update(filterIndex, getUpdatedFilter(updatedGridColumn))
      : R.remove(filterIndex)
    : filter.value
      ? R.append(getUpdatedFilter(updatedGridColumn))
      : R.identity;

  return update(filters);
};

const getUpdatedSort = ({field, sortType}: DataGridColumnWithId) => ({
  field: getDisplaySettingsField(field),
  dir: sortType
});

const updateSorting = (updatedGridColumn: DataGridColumnWithId, sorts: DisplaySettingsSort[] = []): DisplaySettingsSort[] => {
  const {field, sortType, sortAvailable} = updatedGridColumn;

  if (!sortAvailable) {
    return sorts;
  }

  const sortIndex = findIndexByField(getDisplaySettingsField(field), sorts);

  const updateSorts: any = indexFound(sortIndex)
    ? sortType
      ? R.update(sortIndex, getUpdatedSort(updatedGridColumn))
      : R.remove(sortIndex)
    : sortType
      ? R.append(getUpdatedSort(updatedGridColumn))
      : R.identity;

  return updateSorts(sorts);
};

export const defaultGridDisplaySettings: GridDisplaySettings = {
  gridFilter: {filters: []},
  gridSorting: [],
  columns: []
};

const getFilters = R.pathOr([], ['gridFilter', 'filters']);
const getSorting = R.propOr([], 'gridSorting');

export const updateGridDisplaySettings = (updatedGridColumn: DataGridColumnWithId, displaySettings: GridDisplaySettings): GridDisplaySettings => {
  const gridDisplaySettings = displaySettings || defaultGridDisplaySettings;
  return {
    columns: updateColumns(updatedGridColumn, gridDisplaySettings.columns),
    gridFilter: {filters: updateFilters(updatedGridColumn, getFilters(gridDisplaySettings))},
    gridSorting: updateSorting(updatedGridColumn, getSorting(gridDisplaySettings)),
    columnsOrder: gridDisplaySettings?.columnsOrder
  };
};

export const updateGridDisplaySettingsForColumns = (columns: DataGridColumnWithId[], displaySettings: GridDisplaySettings): GridDisplaySettings => {
  const gridDisplaySettings = displaySettings || defaultGridDisplaySettings;
  return R.mergeRight(gridDisplaySettings, {
    columnsOrder: <string[]>R.map(column => column.field, columns)
  });
};
